import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import theme from "../../../theme/theme";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { SelectContext } from "./selectContext";
import Option from "./Option";

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.gray};

  ${({ blue }) =>
    blue &&
    `
  color: grey;
  font-size: 15px;
  border-bottom: 2px solid ${theme.colors.primary};
  padding: 0.5em 0;

  ;`}

  li {
    box-sizing: border-box;
    list-style-type: none;
    padding: 20px;
    border-bottom: 1px solid ${theme.colors.gray};
    cursor: pointer;
    width: 100%;
  }

  li:last-child {
    border-bottom: none;
  }

  li:hover {
    background-color: #d9d9d9;
  }

  .selected-text {
    padding: 10px 0;
    text-align: left;
    cursor: pointer;
    font-size: 1em;
    margin-left: ${(props) => (props.isMarginLeftRequired ? `1.3em` : "")};

    &.disabled {
      cursor: not-allowed;
      color: lightgray;
    }
  }

  .selected-text::after {
    content: "";
    position: absolute;
    right: 10px;
    top: 50%;
    border: 7px solid transparent;
    border-color: black transparent transparent transparent;
  }

  .select-options {
    z-index: 100;
    box-sizing: border-box;
    padding: 10px;
    position: absolute;
    margin-top: 5px;
    max-height: 300px;
    overflow: scroll;
    width: 100%;
    border: 1px solid ${theme.colors.primary};
    background-color: ${theme.colors.white};
    /* hidden by defualt */
    min-height: 0;
    opacity: 0;
    display: none;

    &::-webkit-scrollbar {
      width: 10px;
      border: none;
    }

    &.show {
      min-height: 50px;
      opacity: 1;
      display: block;
    }
  }
`;

const Select = ({
  defaultValue,
  placeholder,
  options,
  onChange,
  disabled = false,
  blue,
  isMarginLeftRequired = false,
}) => {
  const selectContainerRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(defaultValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const selectPlaceholder = placeholder || "Choose an option";

  const clickOutsideHandler = () => setShowDropdown(false);
  useOnClickOutside(selectContainerRef, clickOutsideHandler);

  const updateSelectedOption = (option) => {
    if (onChange) {
      onChange(option);
    }
    setSelectedOption(option);
    setShowDropdown(false);
  };

  useEffect(() => {
    setSelectedOption(defaultValue);
  }, [defaultValue]);

  return (
    <SelectContext.Provider
      value={{ selectedOption, changeSelectedOption: updateSelectedOption }}
    >
      <Wrapper
        blue={blue}
        ref={selectContainerRef}
        isMarginLeftRequired={isMarginLeftRequired}
        key={selectedOption}
      >
        <div
          className={`selected-text ${disabled ? "disabled" : ""}`}
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
        >
          {options.find((opt) => opt.value === selectedOption)?.label ||
            selectPlaceholder}
        </div>
        <ul className={`select-options ${showDropdown ? "show" : ""}`}>
          {options.map((option, index) => (
            <Option key={index} value={option.value}>
              {option.label}
            </Option>
          ))}
        </ul>
      </Wrapper>
    </SelectContext.Provider>
  );
};

export default Select;
